import { DateOnly, DateTime } from "components/common/Date";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import StatusBadge from "components/common/StatusBadge";
import { formatCurrency } from "utils/currency";
import {
  isPayrollRunDraft,
  PayrollRunStatus,
  payrollRunStatusMap,
} from "features/Payroll/constants/payroll.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { usePayrollRunDetail } from "./PayrollRunDetailContext";
import { Alert, Card, CardBody, Col, Row, Table } from "reactstrap";
import type { PayrollRun } from "../types/payroll.types";

function buildPayrollTimeline(run: PayrollRun): TimelineStep[] {
  const history = run.statusHistory ?? [];

  if (history.length === 0) {
    const status = run.status?.toUpperCase() ?? PayrollRunStatus.DRAFT;
    const label = payrollRunStatusMap[status]?.label ?? status;
    const isCancelled = status === PayrollRunStatus.CANCELLED;
    const isDone = status === PayrollRunStatus.FINALIZED || status === PayrollRunStatus.PAID;
    return [{ label, timestamp: run.created_at, state: isCancelled ? "skipped" : isDone ? "done" : "active" }];
  }

  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const status = entry.status?.toUpperCase();
    const label = payrollRunStatusMap[status]?.label ?? entry.status;
    const isCancelled = status === PayrollRunStatus.CANCELLED;
    const isDone = status === PayrollRunStatus.FINALIZED || status === PayrollRunStatus.PAID;
    const state: TimelineStepState = isLast
      ? isCancelled ? "skipped" : isDone ? "done" : "active"
      : "done";
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return { label, timestamp: entry.at, by: byLabel, actor: entry.message ?? undefined, state };
  });
}

const OverviewTab = () => {
  const { can } = useAccess();
  const { run, totals } = usePayrollRunDetail();

  if (!run) return null;

  return (
    <>
      {can(Permissions.PAYROLL.FINALIZE) && isPayrollRunDraft(run.status) && (
        <Alert color="info" className="border-0 mb-3" fade={false}>
          <div className="d-flex gap-2 align-items-center">
            <i className="ri-information-line fs-18 flex-shrink-0" />
            <div>
              <strong>Draft run.</strong> Review the figures below, then
              finalize when you&apos;re ready. After finalizing, this run is
              typically locked for editing.
            </div>
          </div>
        </Alert>
      )}
      {run.status === PayrollRunStatus.CANCELLED && (
        <Alert color="danger" className="border-0 mb-3" fade={false}>
          <div className="d-flex gap-2 align-items-center">
            <i className="ri-close-circle-line fs-18 flex-shrink-0" />
            <div>
              <strong>Cancelled run.</strong>{" "}
              {run.cancelReason?.trim()
                ? `Reason: ${run.cancelReason.trim()}`
                : "This run was cancelled."}
              {run.cancelledAt ? (
                <>
                  {" "}
                  Cancelled on{" "}
                  <span className="fw-medium">
                    <DateOnly date={run.cancelledAt} />
                  </span>
                  .
                </>
              ) : null}
            </div>
          </div>
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={8}>
          <Card>
            <CardBody>
              <h6 className="mb-3 text-uppercase text-muted">Run details</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    <tr>
                      <th
                        className="ps-0 text-muted fw-medium"
                        scope="row"
                        style={{ width: 160 }}
                      >
                        Period
                      </th>
                      <td>
                        <DateOnly date={run.periodStart} />
                        {" – "}
                        <DateOnly date={run.periodEnd} />
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0 text-muted fw-medium" scope="row">
                        Status
                      </th>
                      <td>
                        {run.status ? (
                          <StatusBadge
                            value={run.status}
                            map={payrollRunStatusMap}
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    {run.created_at && (
                      <tr>
                        <th className="ps-0 text-muted fw-medium" scope="row">
                          Generated
                        </th>
                        <td className="text-muted">
                          <DateTime date={run.created_at} />
                        </td>
                      </tr>
                    )}
                    {run.finalizedAt && (
                      <tr>
                        <th className="ps-0 text-muted fw-medium" scope="row">
                          Finalized
                        </th>
                        <td className="text-muted">
                          <DateTime date={run.finalizedAt} />
                        </td>
                      </tr>
                    )}
                    {run.paidAt && (
                      <tr>
                        <th className="ps-0 text-muted fw-medium" scope="row">
                          Paid
                        </th>
                        <td className="text-muted">
                          <DateTime date={run.paidAt} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="border-top border-top-dashed mt-4 pt-3">
                <Row className="g-3">
                  <Col sm={4}>
                    <div>
                      <p className="mb-1 text-uppercase fw-medium text-muted fs-12">
                        Employees
                      </p>
                      <h5 className="fs-16 mb-0">{totals.employees}</h5>
                    </div>
                  </Col>
                  <Col sm={4}>
                    <div>
                      <p className="mb-1 text-uppercase fw-medium text-muted fs-12">
                        Total gross
                      </p>
                      <h5 className="fs-16 mb-0">
                        {formatCurrency(totals.gross)}
                      </h5>
                    </div>
                  </Col>
                  <Col sm={4}>
                    <div>
                      <p className="mb-1 text-uppercase fw-medium text-muted fs-12">
                        Total net
                      </p>
                      <h5 className="fs-16 mb-0 text-success">
                        {formatCurrency(totals.net)}
                      </h5>
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="border-top border-top-dashed mt-4 pt-3">
                <Row className="gy-3">
                  {run.created_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Created</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={run.created_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                  {run.updated_at ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium">Last updated</p>
                        <h5 className="fs-14 mb-0">
                          <DateTime date={run.updated_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <CardBody>
              <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
              <Timeline steps={buildPayrollTimeline(run)} />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default OverviewTab;
